import { Button, Stack } from '@mui/material';
import { Assessment, FilterKeys } from '@schooly/api';
import {
  buildFiltersSearchParams,
  clearLastAppliedFilter,
  StoredFilterSections,
} from '@schooly/components/filters';
import {
  Counter,
  ListAccordion,
  ListAccordionHeader,
  ListRow,
  ListRowClampedText,
  TypographyWithOverflowHint,
} from '@schooly/style';
import { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import {
  ASSESSMENTS_WIDGET_PAGE_SIZE,
  AssessmentsWidgetType,
  useAssessmentsWidgetQuery,
} from './useAssessmentsWidgetQuery';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import { DATE_FORMAT_SHORT } from '@schooly/constants';
import { useCreatorLabel } from '../utils';
import { useAuth } from '@schooly/components/authentication';
import { LIST_ROW_COLUMNS } from '@schooly/constants/test-ids';

const VARIANT_BY_TYPE = {
  'awaiting-completion': { accordion: 'attention', counter: 'attentionSoft' },
  upcoming: { accordion: 'default', counter: 'primarySoft' },
} as const;

export interface AssessmentsWidgetProps {
  type: AssessmentsWidgetType;
}

export const AssessmentsWidget: FC<AssessmentsWidgetProps> = ({ type }) => {
  const { relationId } = useAuth();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { assessments, total, filters } = useAssessmentsWidgetQuery(type);

  const hasMore = total > ASSESSMENTS_WIDGET_PAGE_SIZE;

  const handleShowAll = () => {
    clearLastAppliedFilter(StoredFilterSections.Assessments);
    navigate(
      `/assessments?${buildFiltersSearchParams({
        ...filters,
        ...(type === 'upcoming' && {
          [FilterKeys.RepetitionType]: [],
        }),
      })}`,
    );
  };

  if (!total || !relationId) return null;

  const variant = VARIANT_BY_TYPE[type];

  return (
    <ListAccordion
      header={
        <ListAccordionHeader
          variant={variant.accordion}
          title={formatMessage({ id: `dashboard-teacher-Assessments-${type}` })}
          counter={
            <Counter styleType={variant.counter} size="sm">
              {total}
            </Counter>
          }
        />
      }
    >
      {assessments.map((assessment) => (
        <AssessmentRow
          assessment={assessment}
          key={assessment.id}
          relationId={relationId}
          onRowClick={() => navigate(`/assessments/${assessment.id}`)}
        />
      ))}
      {hasMore && (
        <Stack alignItems="center" py={0.75}>
          <Button variant="text" onClick={handleShowAll}>
            <FormattedMessage id="action-ShowAll" />
          </Button>
        </Stack>
      )}
    </ListAccordion>
  );
};

const AssessmentRow: FC<{ assessment: Assessment; relationId: string; onRowClick: () => void }> = ({
  assessment,
  relationId,
  onRowClick,
}) => {
  const groupsText = assessment.groups.map((g) => g.name).join(', ');

  const creatorLabel = useCreatorLabel(assessment.creator, relationId);
  return (
    <ListRow
      onClick={onRowClick}
      leadingContent={
        <TypographyWithOverflowHint
          data-test-id={LIST_ROW_COLUMNS.date}
          className="list-row-title-secondary"
          variant="h4"
          color="common.grey"
          ml={0.5}
        >
          {format(newDateTimezoneOffset(assessment.assessment_date), DATE_FORMAT_SHORT)}
        </TypographyWithOverflowHint>
      }
      subtitle={
        <TypographyWithOverflowHint
          data-test-id={LIST_ROW_COLUMNS.created_by}
          color="common.grey3"
          className="list-row-title-secondary"
        >
          {creatorLabel}
        </TypographyWithOverflowHint>
      }
      title={
        <TypographyWithOverflowHint
          data-test-id={LIST_ROW_COLUMNS.assessment_name}
          color="common.grey2"
          className="list-row-title"
          variant="h3"
        >
          {assessment.name}
        </TypographyWithOverflowHint>
      }
      actions={
        groupsText ? (
          <ListRowClampedText
            data-test-id={LIST_ROW_COLUMNS.group_names}
            maxWidth={280}
            width={280}
            className="list-row-title"
            lines={2}
            color="common.grey2"
          >
            {groupsText}
          </ListRowClampedText>
        ) : null
      }
      sx={{ '.MuiCardActionArea-root': { height: 54, py: 0.5 } }}
    />
  );
};
